import { useState } from 'react';
import { CloseIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, SimpleGrid, Text, useDisclosure } from '@chakra-ui/react';

import Select from '@components/filter/select';
import DatePickerComp from '@components/datePicker';
import InputComp from '@components/formField/InputComp';
import TextAreaComp from '@components/formField/TextAreaComp';
import { ConditionalComponent } from '@components/functionComponent';
import { dateFormatter, validateObjectFields } from '@helpers/utils';
import { HELPER_TEXT, PLACEHOLDER } from '@pages/ConsumerPromotion/utils/const';
import useGetSchemeList from '@pages/ConsumerPromotion/hooks/query/useGetSchemeList';
import { usePromotionClaim, initialState2 } from '@pages/ConsumerPromotion/hooks/state/usePromotionClaim';
import useMutateCPList from '@pages/ConsumerPromotion/hooks/mutate/useMutateCPList';
import { keys, omit, every, isEmpty, trim } from 'lodash';
import ConfirmModal from '@components/confirmModal';

export default function CreatePromotion({ onClose = () => {} }) {
    const state = usePromotionClaim();
    const { createCP } = useMutateCPList();

    const [invalidFields, setInvalidFields] = useState({});
    const { isOpen, onOpen, onClose: onConfirmClose } = useDisclosure();

    const promotion_scheme = useGetSchemeList();
    const getKeys = keys(initialState2);
    const excludeProperty = ['set', 'reset', 'comment', 'created_by_name', ...getKeys];

    const handleCancel = () => {
        const filtered = omit(state, excludeProperty);
        const allEmpty = every(filtered, (val) => isEmpty(trim(val)));

        if (!allEmpty) {
            onOpen();
        } else {
            onClose();
        }
    };

    const onChange = (field, value) => {
        state.set({ [field]: value });
        const { invalids } = validateObjectFields({ [field]: value }, excludeProperty);
        const mergedInvalids = { ...invalidFields, ...invalids };
        if (isEmpty(invalids)) {
            delete mergedInvalids[field];
        }
        setInvalidFields(mergedInvalids);
    };

    const handleCreate = () => {
        const { invalids, hasInvalid } = validateObjectFields(state, excludeProperty);
        setInvalidFields(invalids);

        if (hasInvalid) return;

        const payload = {
            promotion_name: state.promotion_name,
            scheme_id: state.promotion_scheme.id,
            start_date: dateFormatter(state.start_date, 'YYYY-MM-DD'),
            end_date: dateFormatter(state.end_date, 'YYYY-MM-DD'),
            tfm: state.tfm_id,
        };

        createCP.mutate(payload, {
            onSuccess: () => {
                onClose();
            },
        });
    };

    const handleConfirmYes = () => {
        state.reset();
        onConfirmClose();
        onClose();
    };

    return (
        <Box p={'24px'}>
            <ConditionalComponent statement={isOpen}>
                <ConfirmModal handleConfirmYes={handleConfirmYes} onConfirmClose={onConfirmClose} />
            </ConditionalComponent>

            <ConditionalComponent statement={!isOpen}>
                <Flex justifyContent={'space-between'} alignItems={'center'}>
                    <Text fontWeight={'700'} fontSize={'sm'}>
                        Create Promotion Claim
                    </Text>
                    <Button onClick={handleCancel} variant={'unstyled'}>
                        <CloseIcon fontSize={'10px'} />
                    </Button>
                </Flex>

                <Flex flexDir={'column'} gap={'20px'} py={'32px'}>
                    <TextAreaComp
                        label="Promotion Name"
                        placeholder={PLACEHOLDER.promotion_name}
                        value={state.promotion_name}
                        onChange={(value) => onChange('promotion_name', value)}
                        isInvalid={invalidFields.promotion_name}
                        helperText={invalidFields.promotion_name && HELPER_TEXT.promotion_name}
                        debounceDelay={0}
                    />

                    <SimpleGrid columns={2} spacingY={'20px'} spacingX={'24px'}>
                        <Select
                            title="Promo Scheme"
                            placeholder={PLACEHOLDER.promotion_scheme}
                            isMultiple={false}
                            isCanSearch={false}
                            selected={state.promotion_scheme}
                            refetchData={promotion_scheme?.refetch}
                            data={promotion_scheme?.data?.listScheme}
                            isLoading={promotion_scheme?.isLoading}
                            onChangeFilter={(value) => onChange('promotion_scheme', value)}
                            isInvalid={invalidFields.promotion_scheme}
                            helperText={invalidFields.promotion_scheme && HELPER_TEXT.promotion_scheme}
                        />
                        <InputComp
                            label="No. PM"
                            placeholder={PLACEHOLDER.tfm_id}
                            value={state.tfm_id}
                            onChange={(value) => onChange('tfm_id', value)}
                            isInvalid={invalidFields.tfm_id}
                            helperText={invalidFields.tfm_id && HELPER_TEXT.tfm_id}
                            debounceDelay={0}
                        />
                        <DatePickerComp
                            label="Start Date"
                            value={dateFormatter(state.start_date)}
                            maxDate={state.end_date}
                            isIconRight
                            onChange={(date) => onChange('start_date', date)}
                            isInvalid={invalidFields.start_date}
                            helperText={invalidFields.start_date && HELPER_TEXT.start_date}
                        />

                        <DatePickerComp
                            label="End Date"
                            value={dateFormatter(state.end_date)}
                            minDate={state.start_date}
                            isIconRight
                            onChange={(date) => onChange('end_date', date)}
                            isInvalid={invalidFields.end_date}
                            helperText={invalidFields.end_date && HELPER_TEXT.end_date}
                        />
                    </SimpleGrid>
                </Flex>

                <Flex justifySelf={'end'} gap={'24px'}>
                    <Button variant={'outline'} px={'58px'} onClick={handleCancel}>
                        Batal
                    </Button>
                    <Button px={'58px'} onClick={handleCreate} disabled={createCP.isPending}>
                        Simpan
                    </Button>
                </Flex>
            </ConditionalComponent>
        </Box>
    );
}
